"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "@/components/ui/NavLink";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { acquireOverlay } from "@/lib/overlay-state";
import {
  ArrowRight,
  Menu,
  X,
  ChevronDown,
  House,
  Share2,
  Brain,
  Target,
  Video,
  Film,
  Clapperboard,
  Code2,
  Store,
  Smartphone,
  MonitorSmartphone,
  Megaphone,
  InstagramIcon,
  FacebookIcon,
  LinkedinIcon,
  Mail,
  Palette,
} from "lucide-react";
import { useLang } from "@/lib/LanguageContext";

const SERVICE_DROPDOWN_KEYS = [
  {
    categoryKey: "marketing" as const,
    accent: "#FF419D",
    services: [
      { id: "social-media-management", icon: Share2 },
      { id: "ai-powered-marketing", icon: Brain },
      { id: "meta-ads-campaigns", icon: Target },
    ],
  },
  {
    categoryKey: "creative" as const,
    accent: "#FF953C",
    services: [
      { id: "video-filming", icon: Video },
      { id: "video-editing", icon: Film },
      { id: "ai-video-generation", icon: Clapperboard },
    ],
  },
  {
    categoryKey: "web" as const,
    accent: "#38BDF8",
    services: [
      { id: "custom-websites-nextjs", icon: Code2 },
      { id: "online-store-ecommerce", icon: Store },
      { id: "web-applications", icon: Smartphone },
    ],
  },
];

// Work mega menu — sub-links deep-link into each page's ?cat= filter
const WORK_MENU = [
  {
    key: "websites" as const,
    href: "/work/websites",
    icon: MonitorSmartphone,
    accent: "#38BDF8",
    subs: [
      { cat: "custom-websites", labelKey: "customWebsites" as const, icon: Code2 },
      { cat: "ecommerce", labelKey: "ecommerce" as const, icon: Store },
    ],
  },
  {
    key: "digitalMarketing" as const,
    href: "/work/digital-marketing",
    icon: Megaphone,
    accent: "#FF419D",
    subs: [
      { cat: "visual", labelKey: "visual" as const, icon: Palette },
      { cat: "reels", labelKey: "reels" as const, icon: Film },
    ],
  },
];

const MOBILE_SOCIALS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/aviva.digital",
    icon: InstagramIcon,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/avivadigital.bg",
    icon: FacebookIcon,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/avivadigital/",
    icon: LinkedinIcon,
  },
];

/** BG / EN pill toggle — full size in the desktop header, compact on mobile. */
function LangPill({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale } = useLang();
  return (
    <div
      className="flex items-center gap-0.5 rounded-xl p-0.5"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      {(["bg", "en"] as const).map((lang) => (
        <button
          key={lang}
          onClick={() => setLocale(lang)}
          aria-pressed={locale === lang}
          aria-label={lang === "bg" ? "Български" : "English"}
          className={`relative rounded-[10px] font-bold tracking-widest uppercase transition-all duration-200 ${
            compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-1 text-[13px]"
          } ${
            locale === lang ? "text-white" : "text-white/30 hover:text-white/60"
          }`}
          style={
            locale === lang
              ? {
                  background:
                    "linear-gradient(135deg, rgba(224,64,160,0.25), rgba(255,183,108,0.25))",
                  boxShadow: "0 0 12px rgba(255,183,108,0.2)",
                }
              : {}
          }
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<"services" | "work" | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileWorkOpen, setMobileWorkOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useLang();
  const lastY = useRef(0);
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navLinks = [
    { label: t.nav.clients, href: "/clients" },
    { label: t.nav.about, href: "/about" },
    { label: t.faq.nav, href: "/faq" },
    { label: t.nav.contact, href: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setVisible(y < 80 ? true : y < lastY.current);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Expose header visibility so sticky page bars (e.g. FAQ chips) can sit below it
  useEffect(() => {
    document.documentElement.dataset.header = visible ? "shown" : "hidden";
  }, [visible]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  // Close menus on Escape for keyboard users
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setOpenMenu(null);
        setMobileServicesOpen(false);
        setMobileWorkOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Lock page scroll while the full-screen mobile menu is open; close it if
  // the viewport grows to desktop so the lock never outlives the menu.
  useEffect(() => {
    if (!mobileOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => mq.matches && setMobileOpen(false);
    mq.addEventListener("change", onChange);
    // Hides the chat launcher, which otherwise floats above the open menu
    const releaseOverlay = acquireOverlay();
    return () => {
      html.style.overflow = prev[0];
      body.style.overflow = prev[1];
      mq.removeEventListener("change", onChange);
      releaseOverlay();
    };
  }, [mobileOpen]);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);
  const isServicesActive = pathname.startsWith("/services");
  const isWorkActive = pathname.startsWith("/work");
  const servicesOpen = openMenu === "services";
  const workOpen = openMenu === "work";

  const handleMenuEnter = (menu: "services" | "work") => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setOpenMenu(menu);
  };
  const handleMenuLeave = () => {
    dropdownTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };
  const handleServicesEnter = () => handleMenuEnter("services");
  const handleWorkEnter = () => handleMenuEnter("work");

  // target: a category key or a service id — /services opens the tab and scrolls to it
  const handleCategoryClick = (target: string) => {
    setOpenMenu(null);
    setMobileOpen(false);
    if (pathname.replace(/\/$/, "") === "/services") {
      window.dispatchEvent(
        new CustomEvent("services:category", { detail: target }),
      );
    } else {
      router.push(`/services#${target}`);
    }
  };

  const gradientText = (label: string) => (
    <span style={{ color: "#fff" }}>{label}</span>
  );

  // One shared highlight that glides to whichever nav item is active
  const activePill = (
    <motion.span
      layoutId="nav-active-pill"
      aria-hidden
      className="absolute inset-0 -z-10"
      style={{
        borderRadius: 12,
        background:
          "linear-gradient(135deg, rgba(224,64,160,0.18), rgba(255,183,108,0.18))",
      }}
      transition={{ type: "spring", stiffness: 380, damping: 34 }}
    />
  );

  const categoryIndexMap: Record<string, number> = {
    marketing: 0,
    creative: 1,
    web: 2,
  };
  const getServiceLabel = (id: string) =>
    t.servicesList.find((s) => s.id === id)?.title ?? id;
  const getCategoryLabel = (key: string) =>
    t.categoriesList[categoryIndexMap[key]]?.label ?? key;

  return (
    <>
      {/* ── Desktop floating pill ── */}
      <div
        className="fixed top-4 left-0 right-0 z-50 hidden lg:flex justify-center px-4"
        style={{
          transform: visible ? "translateY(0)" : "translateY(-120%)",
          transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <header
          className="relative flex items-center gap-2 px-3 py-2.5 rounded-[22px]"
          style={{
            background: scrolled
              ? "rgba(10,10,15,0.92)"
              : "rgba(10,10,15,0.78)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: scrolled
              ? "1px solid rgba(255,255,255,0.10)"
              : "1px solid rgba(255,255,255,0.07)",
            boxShadow: scrolled
              ? "0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,183,108,0.06), inset 0 1px 0 rgba(255,255,255,0.06)"
              : "0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          {/* Logo */}
          <Link href="/" className="flex-shrink-0 mr-2">
            <Image
              src="/aviva-digital-white-logo.webp"
              alt="Aviva Digital"
              width={120}
              height={64}
              className="h-16 mt-2 w-auto object-contain"
            />
          </Link>

          <div className="w-px h-5 bg-white/[0.08] mx-1" />

          {/* Nav links */}
          <nav className="flex items-center gap-0.5">
            {/* Home link — a house icon everywhere except on the homepage itself */}
            <Link
              href="/"
              aria-label={t.nav.home}
              title={isActive("/") ? undefined : t.nav.home}
              className={`relative isolate flex items-center px-3.5 py-1.5 rounded-xl text-[13px] font-semibold tracking-[0.08em] uppercase transition-colors duration-200 ${
                isActive("/")
                  ? "text-white"
                  : "text-white/45 hover:text-white/80"
              }`}
            >
              {isActive("/") && activePill}
              {/* Word ↔ house icon: the width glides between the two while they cross-fade,
                  so the whole pill resizes smoothly instead of snapping */}
              <motion.span
                className="relative grid overflow-hidden whitespace-nowrap"
                initial={false}
                animate={{ width: isActive("/") ? "auto" : 16 }}
                transition={{ type: "spring", stiffness: 260, damping: 30 }}
              >
                <motion.span
                  className="[grid-area:1/1] justify-self-start"
                  initial={false}
                  animate={
                    isActive("/")
                      ? { opacity: 1, filter: "blur(0px)", x: 0 }
                      : { opacity: 0, filter: "blur(4px)", x: -6 }
                  }
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {gradientText(t.nav.home)}
                </motion.span>
                <motion.span
                  className="[grid-area:1/1] flex items-center justify-self-start self-center"
                  initial={false}
                  animate={
                    isActive("/")
                      ? { opacity: 0, scale: 0.4, rotate: -45 }
                      : { opacity: 1, scale: 1, rotate: 0 }
                  }
                  transition={{
                    type: "spring",
                    stiffness: 320,
                    damping: 22,
                    delay: isActive("/") ? 0 : 0.08,
                  }}
                >
                  <House size={16} strokeWidth={2} />
                </motion.span>
              </motion.span>
            </Link>

            {/* Services with dropdown */}
            <div
              className="relative"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleMenuLeave}
            >
              <Link
                href="/services"
                onClick={() => setOpenMenu(null)}
                aria-haspopup="true"
                aria-expanded={servicesOpen}
                aria-controls="services-megamenu"
                className={`relative isolate flex items-center gap-1 px-3.5 py-1.5 rounded-xl text-[13px] font-semibold tracking-[0.08em] uppercase transition-colors duration-200 ${
                  isServicesActive
                    ? "text-white"
                    : "text-white/45 hover:text-white/80"
                }`}
              >
                {isServicesActive && activePill}
                {isServicesActive
                  ? gradientText(t.nav.services)
                  : t.nav.services}
                <ChevronDown
                  size={11}
                  strokeWidth={2.5}
                  style={{
                    transform: servicesOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Link>
            </div>

            {/* Work with dropdown */}
            <div
              className="relative"
              onMouseEnter={handleWorkEnter}
              onMouseLeave={handleMenuLeave}
            >
              <Link
                href="/work"
                onClick={() => setOpenMenu(null)}
                aria-haspopup="true"
                aria-expanded={workOpen}
                aria-controls="work-megamenu"
                className={`relative isolate flex items-center gap-1 px-3.5 py-1.5 rounded-xl text-[13px] font-semibold tracking-[0.08em] uppercase transition-colors duration-200 ${
                  isWorkActive
                    ? "text-white"
                    : "text-white/45 hover:text-white/80"
                }`}
              >
                {isWorkActive && activePill}
                {isWorkActive ? gradientText(t.nav.work) : t.nav.work}
                <ChevronDown
                  size={11}
                  strokeWidth={2.5}
                  style={{
                    transform: workOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Link>
            </div>

            {/* Regular nav links */}
            {navLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative isolate px-3.5 py-1.5 rounded-xl text-[13px] font-semibold tracking-[0.08em] uppercase transition-colors duration-200 ${
                    active ? "text-white" : "text-white/45 hover:text-white/80"
                  }`}
                >
                  {active && activePill}
                  {active ? gradientText(link.label) : link.label}
                </Link>
              );
            })}
          </nav>

          <div className="w-px h-5 bg-white/[0.08] mx-1" />

          {/* Language switcher */}
          <LangPill />

          {/* Dropdown — centered on header */}
          {servicesOpen && (
            <>
              {/* Invisible bridge */}
              <div
                className="absolute w-full h-3 z-40"
                style={{ top: "100%", left: 0 }}
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleMenuLeave}
              />
              <div
                id="services-megamenu"
                className="absolute w-[min(820px,calc(100vw-2rem))] rounded-2xl overflow-hidden z-50"
                style={{
                  top: "calc(100% + 16px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(8,8,12,0.98)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.11)",
                  boxShadow:
                    "0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,183,108,0.07)",
                }}
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleMenuLeave}
              >
                <div
                  className="h-px w-full"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,183,108,0.5), rgba(224,64,160,0.4), transparent)",
                  }}
                />
                <div className="grid grid-cols-3 gap-0 p-7">
                  {SERVICE_DROPDOWN_KEYS.map((group) => (
                    <div key={group.categoryKey} className="px-4 flex flex-col">
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.16em] mb-4 pb-3 border-b whitespace-nowrap"
                        style={{
                          color: group.accent,
                          borderColor: `${group.accent}25`,
                        }}
                      >
                        {getCategoryLabel(group.categoryKey)}
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        {group.services.map((svc) => {
                          const Icon = svc.icon;
                          return (
                            <button
                              key={svc.id}
                              onClick={() => handleCategoryClick(svc.id)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] text-white/75 hover:text-white hover:bg-white/[0.04] transition-all duration-150 group text-left"
                            >
                              <span
                                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity"
                                style={{ background: `${group.accent}18` }}
                              >
                                <Icon
                                  size={14}
                                  style={{ color: group.accent }}
                                />
                              </span>
                              <span className="font-medium leading-snug">
                                {getServiceLabel(svc.id)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => handleCategoryClick(group.categoryKey)}
                        className="mt-3 px-3 py-2 text-[12px] font-semibold tracking-wide transition-colors duration-150 text-left"
                        style={{ color: `${group.accent}99` }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = group.accent)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = `${group.accent}99`)
                        }
                      >
                        {t.seeAll}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Work dropdown — Websites / Digital Marketing */}
          {workOpen && (
            <>
              {/* Invisible bridge */}
              <div
                className="absolute w-full h-3 z-40"
                style={{ top: "100%", left: 0 }}
                onMouseEnter={handleWorkEnter}
                onMouseLeave={handleMenuLeave}
              />
              <div
                id="work-megamenu"
                className="absolute w-[min(580px,calc(100vw-2rem))] rounded-2xl overflow-hidden z-50"
                style={{
                  top: "calc(100% + 16px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(8,8,12,0.98)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.11)",
                  boxShadow:
                    "0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,183,108,0.07)",
                }}
                onMouseEnter={handleWorkEnter}
                onMouseLeave={handleMenuLeave}
              >
                <div
                  className="h-px w-full"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,183,108,0.5), rgba(224,64,160,0.4), transparent)",
                  }}
                />
                <div className="grid grid-cols-2 gap-0 p-7">
                  {WORK_MENU.map((group) => (
                    <div key={group.key} className="px-4 flex flex-col">
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.16em] mb-4 pb-3 border-b whitespace-nowrap"
                        style={{
                          color: group.accent,
                          borderColor: `${group.accent}25`,
                        }}
                      >
                        {t.nav[group.key]}
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        {group.subs.map((sub) => {
                          const Icon = sub.icon;
                          return (
                            <Link
                              key={sub.cat}
                              href={`${group.href}?cat=${sub.cat}`}
                              onClick={() => setOpenMenu(null)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] text-white/75 hover:text-white hover:bg-white/[0.04] transition-all duration-150 group text-left"
                            >
                              <span
                                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity"
                                style={{ background: `${group.accent}18` }}
                              >
                                <Icon
                                  size={14}
                                  style={{ color: group.accent }}
                                />
                              </span>
                              <span className="font-medium leading-snug">
                                {t.workFilters[sub.labelKey]}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                      <Link
                        href={group.href}
                        onClick={() => setOpenMenu(null)}
                        className="mt-3 px-3 py-2 text-[12px] font-semibold tracking-wide transition-colors duration-150 text-left"
                        style={{ color: `${group.accent}99` }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = group.accent)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = `${group.accent}99`)
                        }
                      >
                        {t.seeAll}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </header>
      </div>

      {/* ── Mobile/Tablet header ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 lg:hidden"
        style={{
          transform:
            visible || mobileOpen ? "translateY(0)" : "translateY(-110%)",
          transition:
            "transform 0.4s cubic-bezier(0.4,0,0.2,1), background-color 0.3s, border-color 0.3s",
          backgroundColor: mobileOpen
            ? "rgba(10,10,15,0)"
            : "rgba(10,10,15,0.85)",
          backdropFilter: mobileOpen ? "none" : "blur(28px) saturate(180%)",
          WebkitBackdropFilter: mobileOpen
            ? "none"
            : "blur(28px) saturate(180%)",
          borderBottom: `1px solid ${mobileOpen ? "rgba(255,255,255,0)" : "rgba(255,255,255,0.07)"}`,
        }}
      >
        <div className="grid grid-cols-[1fr_auto_1fr] items-center px-4 h-16">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="-ml-2.5 justify-self-start relative flex h-11 w-11 items-center justify-center text-white/75 hover:text-white transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={mobileOpen ? "close" : "open"}
                initial={{
                  opacity: 0,
                  rotate: mobileOpen ? -90 : 90,
                  scale: 0.7,
                }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: mobileOpen ? 90 : -90, scale: 0.7 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="flex"
              >
                {mobileOpen ? (
                  <X size={22} strokeWidth={1.75} />
                ) : (
                  <Menu size={22} strokeWidth={1.75} />
                )}
              </motion.span>
            </AnimatePresence>
          </button>
          <Link
            href="/"
            className="justify-self-center"
            onClick={() => setMobileOpen(false)}
          >
            <Image
              src="/aviva-digital-white-logo.webp"
              alt="Aviva Digital"
              width={130}
              height={52}
              className="h-[52px] w-auto object-contain"
            />
          </Link>
          {/* Closed: quick-contact sparkle; open: language switch */}
          <div className="justify-self-end">
            <AnimatePresence initial={false} mode="wait">
              {mobileOpen ? (
                <motion.div
                  key="lang"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  <LangPill compact />
                </motion.div>
              ) : (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  <Link
                    href="/contact"
                    aria-label={t.cta}
                    className="relative flex h-10 w-10 items-center justify-center rounded-full border border-transparent"
                    style={{
                      background:
                        "linear-gradient(#0E0C12, #0E0C12) padding-box, linear-gradient(135deg, #E040A0, #FFB76C) border-box",
                    }}
                  >
                    <Mail
                      size={17}
                      strokeWidth={1.75}
                      className="text-white/85"
                    />
                    {/* Blinking "new message" dot */}
                    <span
                      aria-hidden
                      className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                    >
                      <span className="absolute inset-0 animate-ping rounded-full bg-[#FF7F86] opacity-70" />
                      <span
                        className="relative h-2.5 w-2.5 rounded-full border border-[#0A0A0F]"
                        style={{
                          background:
                            "linear-gradient(135deg, #E040A0, #FFB76C)",
                        }}
                      />
                    </span>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* ── Mobile/Tablet full-screen menu ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.25, delay: 0.05 } }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-[#0A0A0F] lg:hidden"
          >
            {/* Single soft ambient glow */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div
                className="absolute -top-40 -right-32 h-[380px] w-[380px] rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(224,64,160,0.13), transparent 65%)",
                }}
              />
              <div
                className="absolute -bottom-48 -left-40 h-[420px] w-[420px] rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,183,108,0.07), transparent 65%)",
                }}
              />
            </div>

            <nav
              id="mobile-nav"
              aria-label="Mobile"
              className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-7 pt-[calc(3.5rem+2.5rem)] pb-[max(1.75rem,env(safe-area-inset-bottom))]"
            >
              <motion.ul
                className="flex flex-col gap-1"
                initial="hidden"
                animate="show"
                variants={{
                  show: {
                    transition: { staggerChildren: 0.045, delayChildren: 0.08 },
                  },
                }}
              >
                {[
                  { label: t.nav.home, href: "/", active: isActive("/") },
                  {
                    label: t.nav.services,
                    href: "/services",
                    active: isServicesActive,
                    services: true,
                  },
                  {
                    label: t.nav.work,
                    href: "/work",
                    active: isWorkActive,
                    work: true,
                  },
                  ...navLinks.map((l) => ({ ...l, active: isActive(l.href) })),
                ].map((item) => (
                  <motion.li
                    key={item.href}
                    variants={{
                      hidden: { opacity: 0, y: 14 },
                      show: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.45,
                          ease: [0.22, 1, 0.36, 1],
                        },
                      },
                    }}
                  >
                    <div className="flex items-center">
                      <Link
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        aria-current={item.active ? "page" : undefined}
                        className="group relative flex items-center py-2.5"
                      >
                        {item.active && (
                          <span
                            aria-hidden
                            className="absolute -left-7 top-1/2 h-px w-4 -translate-y-1/2"
                            style={{
                              background:
                                "linear-gradient(90deg, #E040A0, #FFB76C)",
                            }}
                          />
                        )}
                        <span
                          className={`font-display text-[30px] font-medium leading-tight tracking-tight transition-colors duration-200 ${
                            item.active
                              ? "text-gradient-warm"
                              : "text-white/55 group-hover:text-white"
                          }`}
                        >
                          {item.label}
                        </span>
                      </Link>
                      {(item.services || item.work) &&
                        (() => {
                          const expanded = item.services
                            ? mobileServicesOpen
                            : mobileWorkOpen;
                          const name = item.services ? "services" : "work";
                          return (
                            <button
                              onClick={() =>
                                item.services
                                  ? setMobileServicesOpen(!mobileServicesOpen)
                                  : setMobileWorkOpen(!mobileWorkOpen)
                              }
                              className="ml-1 flex h-11 w-11 items-center justify-center text-white/35 transition-colors hover:text-white"
                              aria-label={
                                expanded ? `Collapse ${name}` : `Expand ${name}`
                              }
                              aria-expanded={expanded}
                              aria-controls={`mobile-${name}-list`}
                            >
                              <ChevronDown
                                size={18}
                                strokeWidth={1.5}
                                className="transition-transform duration-300"
                                style={{
                                  transform: expanded
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                }}
                              />
                            </button>
                          );
                        })()}
                    </div>

                    {item.services && (
                      <AnimatePresence initial={false}>
                        {mobileServicesOpen && (
                          <motion.div
                            id="mobile-services-list"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              duration: 0.35,
                              ease: [0.22, 1, 0.36, 1],
                            }}
                            className="overflow-hidden"
                          >
                            <div className="flex flex-col gap-4 border-l border-white/[0.08] py-2 pb-4 pl-4">
                              {SERVICE_DROPDOWN_KEYS.map((group) => (
                                <div key={group.categoryKey}>
                                  <button
                                    onClick={() =>
                                      handleCategoryClick(group.categoryKey)
                                    }
                                    // Same look as the hero "Portfolio" outline button, tinted per category
                                    className="mb-1.5 inline-flex items-center rounded-full border px-4 py-1.5 font-display text-[14px] font-semibold backdrop-blur-sm transition-opacity hover:opacity-90"
                                    style={{
                                      borderColor: group.accent,
                                      color: group.accent,
                                      background: `${group.accent}1a`,
                                    }}
                                  >
                                    {getCategoryLabel(group.categoryKey)}
                                  </button>
                                  <div className="flex flex-col">
                                    {group.services.map((svc) => (
                                      <button
                                        key={svc.id}
                                        onClick={() =>
                                          handleCategoryClick(svc.id)
                                        }
                                        className="flex items-center gap-2.5 py-1.5 text-left text-[17px] text-white/65 transition-colors hover:text-white"
                                      >
                                        <span
                                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                                          style={{ background: group.accent }}
                                        />
                                        {getServiceLabel(svc.id)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}

                    {item.work && (
                      <AnimatePresence initial={false}>
                        {mobileWorkOpen && (
                          <motion.div
                            id="mobile-work-list"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              duration: 0.35,
                              ease: [0.22, 1, 0.36, 1],
                            }}
                            className="overflow-hidden"
                          >
                            <div className="flex flex-col gap-2 border-l border-white/[0.08] py-2 pb-4 pl-4">
                              {WORK_MENU.map((group) => (
                                <div key={group.key}>
                                  <Link
                                    href={group.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="mb-1 flex items-center gap-2 py-1 text-[17px] font-medium text-white/80 transition-colors hover:text-white"
                                  >
                                    <span
                                      className="h-1.5 w-1.5 rounded-full"
                                      style={{ background: group.accent }}
                                    />
                                    {t.nav[group.key]}
                                  </Link>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </motion.li>
                ))}
              </motion.ul>

              {/* Footer: CTA, language, socials */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="mt-auto flex flex-col gap-6 pt-10"
              >
                <Link
                  href="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="group flex h-12 w-full items-center justify-center gap-2 rounded-full border border-transparent font-display text-[15px] font-medium text-white"
                  style={{
                    background:
                      "linear-gradient(#0E0C12, #0E0C12) padding-box, linear-gradient(135deg, #E040A0, #FFB76C) border-box",
                  }}
                >
                  {t.cta}
                  <ArrowRight
                    size={15}
                    strokeWidth={2}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </Link>

                {/* Language switch lives in the top bar while the menu is open */}
                <div className="flex items-center border-t border-white/[0.06] pt-5">
                  <div className="-ml-2.5 flex items-center">
                    {MOBILE_SOCIALS.map(({ label, href, icon: Icon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="flex h-10 w-10 items-center justify-center text-white/40 transition-colors hover:text-white"
                      >
                        <Icon size={17} strokeWidth={1.5} />
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
